import { createRouter, createWebHistory } from "vue-router";
import BusinessLookupComponent from "./components/Pages/BusinessLookupComponent.vue";
import FormComponent from "./components/Pages/FormComponent.vue";
import CompletedComponent from "./components/Pages/CompletedComponent.vue";
import PasswordResetComponent from "./components/Pages/PasswordResetComponent.vue";
import LandingPageComponent from "./components/Pages/LandingPageComponent.vue";
import SignUpSuccessComponent from "./components/Pages/SignUpSuccessComponent.vue";
import SignUpCancelledComponent from "./components/Pages/SignUpCancelledComponent.vue";
import BusinessSignUpComponent from "./components/Pages/BusinessSignUpComponent.vue";
import BusinessSignInComponent from "./components/Pages/BusinessSignInComponent.vue";
import RegisterBusinessComponent from "./components/Pages/RegisterBusinessComponent.vue";
import DashboardShell from "./components/Organisms/DashboardShell.vue";
import DashboardInsightsComponent from "./components/Pages/DashboardInsightsComponent.vue";
import DashboardWebPageComponent from "./components/Pages/DashboardWebPageComponent.vue";
import DashboardSettingsComponent from "./components/Pages/DashboardSettingsComponent.vue";
import DashboardEmployeesComponent from "./components/Pages/DashboardEmployeesComponent.vue";
import DashboardAccountComponent from "./components/Pages/DashboardAccountComponent.vue";
import { track } from "./analytics.js";
import {
  authStore,
  whenAuthReady,
  refreshEmployers,
} from "./stores/authStore.js";
import { ensureSelectedBusiness } from "./stores/businessContext.js";

const routes = [
  {
    path: "/",
    component: LandingPageComponent,
    name: "home",
    meta: { title: "Lulu Rewards" },
  },
  {
    path: "/business/:businessId",
    component: FormComponent,
    name: "form",
    props: true,
    meta: { title: "Lulu Rewards", hideHeader: true },
  },
  {
    path: "/business",
    component: BusinessLookupComponent,
    name: "business-lookup",
    props: true,
    meta: { title: "Lulu Rewards" },
  },
  {
    path: "/completed",
    component: CompletedComponent,
    name: "completed",
    props: true,
    meta: { title: "Success!" },
  },
  {
    path: "/passwordreset",
    component: PasswordResetComponent,
    name: "passwordreset",
    props: true,
    meta: { title: "Password Reset" },
  },
  {
    path: "/signup-success",
    component: SignUpSuccessComponent,
    name: "signup-success",
    meta: { title: "Welcome to Lulu Rewards!" },
  },
  {
    path: "/signup-cancelled",
    component: SignUpCancelledComponent,
    name: "signup-cancelled",
    meta: { title: "Setup Not Completed" },
  },
  {
    path: "/business-signup",
    component: BusinessSignUpComponent,
    name: "business-signup",
    meta: { title: "Create a business account", hideHeader: true },
  },
  {
    path: "/business-signin",
    component: BusinessSignInComponent,
    name: "business-signin",
    meta: { title: "Sign in", hideHeader: true },
  },
  {
    path: "/register-business",
    component: RegisterBusinessComponent,
    name: "register-business",
    meta: { title: "Register your business", hideHeader: true, requiresAuth: true },
  },
  {
    path: "/dashboard",
    component: DashboardShell,
    meta: {
      title: "Dashboard",
      hideHeader: true,
      requiresAuth: true,
      requiresEmployer: true,
    },
    children: [
      { path: "", redirect: { name: "dashboard-insights" } },
      {
        path: "insights",
        name: "dashboard-insights",
        component: DashboardInsightsComponent,
        meta: { title: "Insights - Lulu Rewards" },
      },
      {
        path: "web-page",
        name: "dashboard-web-page",
        component: DashboardWebPageComponent,
        meta: { title: "Sign-up page - Lulu Rewards" },
      },
      {
        path: "settings",
        name: "dashboard-settings",
        component: DashboardSettingsComponent,
        meta: { title: "Settings - Lulu Rewards" },
      },
      {
        path: "employees",
        name: "dashboard-employees",
        component: DashboardEmployeesComponent,
        meta: { title: "Team - Lulu Rewards" },
      },
      {
        path: "account",
        name: "dashboard-account",
        component: DashboardAccountComponent,
        meta: { title: "Account - Lulu Rewards" },
      },
    ],
  },
  // Catch-all route to redirect unknown paths to home
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const AUTH_ROUTE_NAMES = new Set(["business-signin", "business-signup"]);

router.beforeEach(async (to) => {
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth);
  const requiresEmployer = to.matched.some((r) => r.meta.requiresEmployer);
  const isAuthRoute = AUTH_ROUTE_NAMES.has(to.name);

  // Wait for Firebase to emit its first onAuthStateChanged so we never
  // decide on a stale `user`. This is the hard-refresh fix.
  await whenAuthReady();
  const user = authStore.user;

  if ((requiresAuth || requiresEmployer) && !user) {
    return {
      path: "/business-signin",
      query: { redirect: to.fullPath },
    };
  }

  if (isAuthRoute && user) {
    const ids = authStore.employerBusinessIds;
    if (Array.isArray(ids) && ids.length > 0) {
      return "/dashboard/insights";
    }
    return "/register-business";
  }

  if (to.name === "home" && user) {
    if (authStore.employerBusinessIds === null) {
      await refreshEmployers();
    }
    const ids = authStore.employerBusinessIds || [];
    if (ids.length > 0) {
      return "/dashboard/insights";
    }
    return "/register-business";
  }

  if (requiresEmployer && user) {
    if (authStore.employerBusinessIds === null) {
      await refreshEmployers();
    }
    const ids = authStore.employerBusinessIds || [];
    if (ids.length === 0) {
      return "/register-business";
    }
    ensureSelectedBusiness(user.email, ids);
  }

  if (to.name === "register-business" && user) {
    if (authStore.employerBusinessIds === null) {
      await refreshEmployers();
    }
    const ids = authStore.employerBusinessIds || [];
    if (ids.length > 0) {
      return "/dashboard/insights";
    }
  }

  return true;
});

router.afterEach((to) => {
  track("page_viewed", { path: to.path, route_name: to.name });
});

export default router;
